import { useState } from "react";
import { Button } from "../ui/button";
import { Loader2, Shield, Coffee, Briefcase, UtensilsCrossed, Navigation, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { haptic } from "../../utils/haptic";

/**
 * EARLY ARRIVAL RECOVERY BLOCK
 * Solves the 4AM Dead Zone problem - when flight lands before hotel check-in
 */
const EarlyArrivalRecoveryBlock = ({ note, hotelCheckinTime }) => {
  const [requestingEarlyCheckin, setRequestingEarlyCheckin] = useState(false);
  const [earlyCheckinRequested, setEarlyCheckinRequested] = useState(false);
  
  const handleRequestEarlyCheckin = async () => {
    setRequestingEarlyCheckin(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setRequestingEarlyCheckin(false);
    setEarlyCheckinRequested(true);
    toast.success("Early check-in requested! Hotel will confirm within 24h.");
    haptic("success");
  };
  
  const recoveryIcons = {
    lounge: Coffee,
    bag: Briefcase,
    diner: UtensilsCrossed,
    explore: Navigation
  };
  
  return (
    <div 
      className="mb-6 rounded-2xl overflow-hidden border-2 border-orange-500/50"
      style={{ boxShadow: '0 0 25px rgba(249, 115, 22, 0.2)' }}
      data-testid="early-arrival-recovery-block"
    >
      {/* Header - High Priority Alert */}
      <div className="bg-gradient-to-r from-orange-500/20 to-red-500/10 px-5 py-3 border-b border-orange-500/30">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center animate-pulse">
            <BedDouble className="w-5 h-5 text-white" />
          </div>
          <div>
            <div 
              className="text-sm font-black text-orange-400 uppercase tracking-wider"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              {note.title}
            </div>
            <div className="text-xs text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
              ~{note.hours_gap}h until standard check-in
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="bg-card/80 p-5">
        {/* Timeline Visual */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex flex-col items-center">
            <div className="w-3 h-3 rounded-full bg-cyan-400" style={{ boxShadow: '0 0 8px rgba(34, 211, 238, 0.6)' }} />
            <div className="w-0.5 h-8 bg-gradient-to-b from-cyan-400 to-orange-500" />
            <div className="w-3 h-3 rounded-full bg-orange-500" style={{ boxShadow: '0 0 8px rgba(249, 115, 22, 0.6)' }} />
            <div className="w-0.5 h-8 bg-gradient-to-b from-orange-500 to-purple-500" />
            <div className="w-3 h-3 rounded-full bg-purple-500" style={{ boxShadow: '0 0 8px rgba(168, 85, 247, 0.6)' }} />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-cyan-400" style={{ fontFamily: "'Space Mono', monospace" }}>
                Flight Lands
              </span>
              <span className="text-sm font-bold text-cyan-400" style={{ fontFamily: "'Space Mono', monospace" }}>
                {note.arrival_time}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-orange-400" style={{ fontFamily: "'Space Mono', monospace" }}>
                Recovery Zone
              </span>
              <span className="text-sm font-bold text-orange-400" style={{ fontFamily: "'Space Mono', monospace" }}>
                SAFE LANDING
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-purple-400" style={{ fontFamily: "'Space Mono', monospace" }}>
                Hotel Check-in
              </span>
              <span className="text-sm font-bold text-purple-400" style={{ fontFamily: "'Space Mono', monospace" }}>
                {hotelCheckinTime}
              </span>
            </div>
          </div>
        </div>
        
        {/* Recovery Options */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {note.recovery_options?.map((option, idx) => {
            const IconComponent = recoveryIcons[option.icon] || Coffee;
            return (
              <div 
                key={idx}
                className="p-3 rounded-xl bg-muted/30 border border-border text-center hover:border-orange-500/50 transition-colors cursor-pointer"
              >
                <IconComponent className="w-4 h-4 mx-auto mb-1 text-orange-400" />
                <div className="text-[10px] text-muted-foreground" style={{ fontFamily: "'Space Mono', monospace" }}>
                  {option.label}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Recommendation */}
        <div className="text-xs text-muted-foreground mb-4 italic" style={{ fontFamily: "'Space Mono', monospace" }}>
          {note.recommendation}
        </div>
        
        {/* CTA Button */}
        <Button
          onClick={handleRequestEarlyCheckin}
          disabled={requestingEarlyCheckin || earlyCheckinRequested}
          className={`w-full ${earlyCheckinRequested 
            ? 'bg-green-500/20 text-green-400 border-green-500/50' 
            : 'bg-orange-500 hover:bg-orange-600 text-white'
          } font-bold uppercase tracking-wider`}
          style={{ fontFamily: "'Space Mono', monospace" }}
          data-testid="request-early-checkin-btn"
        >
          {requestingEarlyCheckin ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Requesting...
            </>
          ) : earlyCheckinRequested ? (
            <>
              <Shield className="w-4 h-4 mr-2" />
              Early Check-in Requested ✓
            </>
          ) : (
            <>
              <BedDouble className="w-4 h-4 mr-2" />
              Request Early Check-in
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EarlyArrivalRecoveryBlock;
